import Phaser from 'phaser';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { RunManager } from '../systems/RunManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE DES CRÉDITS ===

export class CreditsScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private returnScene = 'SettingsScene';
  private returnData: Record<string, unknown> = {};

  constructor() {
    super({ key: 'CreditsScene' });
  }

  init(data: { returnTo?: string; camp?: unknown; meta?: unknown }): void {
    this.returnScene = data?.returnTo ?? 'SettingsScene';
    this.returnData = {};
    if (data?.camp) this.returnData.camp = data.camp;
    if (data?.meta) this.returnData.meta = data.meta;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    this.add.text(GAME_CX, 30, '🏴‍☠️ CRÉDITS', {
      fontSize: '22px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    this.panel = new ScrollablePanel(this, 0, 60, GAME_W, GAME_H - 80);
    let y = 10;

    const title = (text: string) => {
      this.panel.add(this.add.text(GAME_CX, y, text, {
        fontSize: '16px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(0.5));
      y += 25;
    };

    const line = (text: string, color = '#ecf0f1', size = '12px') => {
      this.panel.add(this.add.text(GAME_CX, y, text, {
        fontSize: size, color, fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 20;
    };

    const sep = () => {
      this.panel.add(this.add.rectangle(GAME_CX, y + 5, 400, 1, 0x333333));
      y += 20;
    };

    title('DEADTIDE — The Undead Archipelago');
    y += 10;
    sep();

    line('🎮 Game Design & Direction', '#f39c12', '13px');
    // Sprint 81 — Easter egg : clic sur "Joris Tremoulet" → combat secret Captain Jack
    const creatorTxt = this.add.text(GAME_CX, y, 'Joris Tremoulet', {
      fontSize: '12px', color: '#ecf0f1', fontFamily: FONT_BODY,
    }).setOrigin(0.5);
    creatorTxt.setInteractive({ useHandCursor: true });
    creatorTxt.on('pointerdown', () => this.confirmJackBattle());
    this.panel.add(creatorTxt);
    y += 20;
    y += 10;

    line('💻 Développement', '#f39c12', '13px');
    line('Joris Tremoulet - Nebula');
    y += 10;

    line('🎵 Musique', '#f39c12', '13px');
    line('Forge From Ashes');
    line('Générée par AI', '#7f8c8d', '10px');
    y += 10;

    line('🎨 Art', '#f39c12', '13px');
    line('Sprites SVG générés par AI');
    y += 10;

    line('🔊 Sound Design', '#f39c12', '13px');
    line('SFX procéduraux (Web Audio API)');
    y += 10;

    sep();

    line('Pistes musicales :', '#f39c12', '13px');
    const tracks = [
      'The Cursed Shores', 'Safe Harbor', 'Charting the Unknown',
      "Dead Man's Fight", "Leviathan's Wrath", 'Tides of Fortune', 'Into the Deep',
    ];
    for (const t of tracks) {
      line(`♪ ${t}`, '#8899aa', '11px');
    }
    y += 10;

    sep();

    line('Fait avec :', '#7f8c8d', '11px');
    line('Phaser 3 • TypeScript • Vite', '#7f8c8d', '11px');
    y += 15;

    line('© 2026 Joris Tremoulet - Nebula', '#7f8c8d', '10px');
    y += 30;

    this.panel.setContentHeight(y);

    // Bouton retour
    const backBtn = this.add.text(GAME_CX, 660, '[ Retour ]', {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 16, y: 8 },
    }).setOrigin(0.5);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, this.returnScene, this.returnData));
  }

  shutdown(): void {
    this.panel?.destroy();
  }

  /** Sprint 81 — Easter egg : confirmation avant combat Captain Jack. */
  private confirmJackBattle(): void {
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.85).setDepth(200).setInteractive();
    const elements: Phaser.GameObjects.GameObject[] = [overlay];

    elements.push(this.add.text(GAME_CX, GAME_CY - 80, '⚔️ Le Capitaine Jack Warraps', {
      fontSize: '15px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201));

    elements.push(this.add.text(GAME_CX, GAME_CY - 40,
      'Tu sembles vouloir provoquer le créateur en duel.\nUn affrontement 1v1 t\'attend dans la forge.\nEs-tu prêt, Capitaine ?', {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY, align: 'center',
    }).setOrigin(0.5).setDepth(201));

    const acceptBtn = this.add.text(GAME_CX - 70, GAME_CY + 40, '[ Le défier ]', {
      fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(201);
    acceptBtn.setInteractive({ useHandCursor: true });
    acceptBtn.on('pointerdown', () => {
      elements.forEach(e => e.destroy());
      // Sprint 82 v3 — utilise le flux normal RunPrep → Map → Combat
      const meta = new MetaManager();
      const camp = new CampManager();
      RunManager.clearSavedRun();
      const run = RunManager.createJackDuelRun(meta, camp);
      startSceneWithFade(this, 'RunPrepScene', { camp, meta, presetRun: run });
    });
    elements.push(acceptBtn);

    const cancelBtn = this.add.text(GAME_CX + 70, GAME_CY + 40, '[ Pas aujourd\'hui ]', {
      fontSize: '13px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(201);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => elements.forEach(e => e.destroy()));
    elements.push(cancelBtn);
  }
}
